
import { useState } from 'react';

// Sample doctor data (would typically come from an API)
export interface DoctorData {
  id: number;
  name: string;
  specialty: string;
  image: string;
  rating: number;
  reviews: number;
  bio: string;
  clinic: string;
  clinicAddress: string;
  workingHours: string;
  services: {
    id: number;
    name: string;
    price: number;
    duration: string;
  }[];
  isFavorite: boolean;
  clinicId?: number;
  beforeAfterGallery: {
    id: number;
    title: string;
    image: string;
    views?: number;
    description?: string;
    treatmentType?: string;
  }[];
  stories: {
    id: number;
    title: string;
    image: string;
    description?: string;
    views?: number;
    likes?: number;
    createdAt?: Date;
    expiresAt?: Date;
  }[];
  statistics?: {
    totalStoryViews: number;
    topPerformingStoryId: number;
    profileVisitsThisMonth: number;
    totalGalleryViews: number;
  };
  education: string[];
  experience: string[];
  languages: string[];
  // New fields for professional details
  licenseNumber?: string;
  boardCertifications?: string[];
  insuranceAccepted?: string[];
  hospitalAffiliations?: string[];
  specializations?: string[];
}

// This would typically fetch from an API based on the ID
export const useDoctorData = (id: string | number): { 
  doctorData: DoctorData; 
  toggleFavorite: () => void;
  updateDoctorData: (updates: Partial<DoctorData>) => void;
} => {
  // Sample data - in a real app would be fetched from an API
  const sampleData: DoctorData = {
    id: 1,
    name: "Dr. James Wilson",
    specialty: "Cosmetic Dentist",
    image: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400&h=400&fit=crop",
    rating: 4.9,
    reviews: 127,
    bio: "Dr. Wilson is a highly skilled cosmetic dentist with over 15 years of experience. He specializes in smile makeovers, veneers, and full mouth rehabilitations.",
    clinic: "Bright Smile Dental Clinic",
    clinicAddress: "123 Dental Street, New York, NY",
    workingHours: "Mon-Fri: 9am-5pm",
    clinicId: 1,
    services: [
      {
        id: 1,
        name: "Teeth Whitening",
        price: 299,
        duration: "1 hour"
      },
      {
        id: 2,
        name: "Dental Implants",
        price: 1500,
        duration: "2-3 hours"
      },
      {
        id: 3,
        name: "Veneers",
        price: 800,
        duration: "1.5 hours"
      },
      {
        id: 4,
        name: "Smile Makeover",
        price: 3000,
        duration: "Multiple sessions"
      }
    ],
    isFavorite: false,
    beforeAfterGallery: [
      {
        id: 1,
        title: "Veneer Transformation",
        image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=600&h=400&fit=crop",
        views: 245,
        description: "Complete smile makeover using porcelain veneers",
        treatmentType: "Cosmetic Dentistry"
      },
      {
        id: 2,
        title: "Implant Case",
        image: "https://images.unsplash.com/photo-1606811971618-4486d14f3f99?w=600&h=400&fit=crop",
        views: 187,
        description: "Full mouth rehabilitation with dental implants",
        treatmentType: "Implant Dentistry"
      },
      {
        id: 3,
        title: "Full Smile Makeover",
        image: "https://images.unsplash.com/photo-1595748105476-5680d006e55a?w=600&h=400&fit=crop",
        views: 321,
        description: "Combination of whitening, veneers and crowns",
        treatmentType: "Smile Design"
      }
    ],
    stories: [
      {
        id: 1,
        title: "Advanced Implants",
        image: "https://images.unsplash.com/photo-1649972904349-6e44c42644a7?w=400&h=400&fit=crop",
        description: "New all-on-4 implant technique",
        views: 158,
        likes: 42,
        createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
        expiresAt: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)
      },
      {
        id: 2,
        title: "Whitening Process",
        image: "https://images.unsplash.com/photo-1591492686430-b107ea4c9170?w=400&h=400&fit=crop",
        description: "In-office vs. at-home whitening",
        views: 103,
        likes: 27,
        createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
        expiresAt: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)
      },
      {
        id: 3,
        title: "Smile Design",
        image: "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=400&h=400&fit=crop",
        description: "Digital smile design process",
        views: 212,
        likes: 56,
        createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
        expiresAt: new Date(Date.now() + 6 * 24 * 60 * 60 * 1000)
      }
    ],
    statistics: {
      totalStoryViews: 473,
      topPerformingStoryId: 3,
      profileVisitsThisMonth: 1247,
      totalGalleryViews: 753
    },
    education: [
      "DDS, New York University College of Dentistry",
      "Residency in Advanced Prosthodontics, Columbia University",
      "Certification in Implant Dentistry, American Academy of Implant Dentistry"
    ],
    experience: [
      "15+ years of clinical practice",
      "Former Clinical Director at Manhattan Dental Center (2010-2018)",
      "Guest lecturer at NYU Dental School"
    ],
    languages: ["English", "Spanish", "French"],
    // Professional details
    licenseNumber: "DDS12345-NY",
    boardCertifications: [
      "American Board of Dental Specialties",
      "Board of Cosmetic Dentistry"
    ],
    insuranceAccepted: [
      "Aetna", 
      "Blue Cross", 
      "Cigna", 
      "Delta Dental"
    ],
    hospitalAffiliations: [
      "New York Presbyterian",
      "Mount Sinai Hospital"
    ],
    specializations: [
      "Cosmetic Dentistry",
      "Implants",
      "Smile Design"
    ]
  };

  const [doctorData, setDoctorData] = useState<DoctorData>(sampleData);

  const toggleFavorite = () => {
    setDoctorData(prev => ({
      ...prev,
      isFavorite: !prev.isFavorite
    }));
  };

  const updateDoctorData = (updates: Partial<DoctorData>) => {
    setDoctorData(prev => ({
      ...prev,
      ...updates
    }));
  };

  return { doctorData, toggleFavorite, updateDoctorData };
};
